import { useMemo } from "react";
import {
  DefaultColumnFilter,
  SelectColumnFilter,
  getCommonFilterTypes,
  StatusColumnFilter,
  CodStatusColumnFilter,
  alphaNumericCaseInsensitive,
} from "../../../../helpers/commonTableFunctions";
import {
  useTable,
  usePagination,
  useFilters,
  useSortBy,
  useExpanded,
  useResizeColumns,
  useAbsoluteLayout,
  useBlockLayout,
  useFlexLayout,
} from "react-table";
import SortArrow from "../../../Utility/SortArrow";
import TablePagination from "../../../TablePagination";
import { format as dateFormat, parseISO } from "date-fns";
import { viewStates } from "../../../../constants";
import { PageLoadError } from "../../../Utility/PageLoadError";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const OperationLogsTable = ({ operationLogs, viewState }) => {
  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
  const filterTypes = useMemo(
    () => ({
      ...getCommonFilterTypes(),
    }),
    []
  );
  const alphaNumericCaseInsensitiveSortFunction = useMemo(() => {
    return alphaNumericCaseInsensitive;
  }, []);
  const tableData = useMemo(() => {
    return operationLogs;
  }, [operationLogs]);

  const tableColumns = useMemo(
    () => [
      {
        Header: "Log Type",
        accessor: "type",
        Filter: SelectColumnFilter,
        width: 90,
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Delivery ID",
        accessor: "dtranid",
        width: 90,
        filter: "includesTrimmed",
      },

      {
        Header: "Invoice",
        accessor: "invoice",
        width: 90,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Customer",
        accessor: "customer",
        width: 120,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "City",
        accessor: "city",
        width: 100,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Comment",
        accessor: "comment",
        width: 180,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Note",
        Cell: (cellData) => {
          if (cellData.row.original.type === "Over/Shortage") {
            return "-";
          } else if (cellData.row.original.type === "Redelivery") {
            return (
              <>
                Del Time <br /> {cellData.value}
              </>
            );
          } else if (cellData.row.original.type === "Damage") {
            return (
              <>
                Part Numbers <br /> {cellData.value}
              </>
            );
          } else {
            return cellData.value;
          }
        },
        accessor: "note",
        width: 100,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Manifest",
        Cell: (cellData) => {
          return cellData.row.original.type === "Over/Shortage"
            ? cellData.value
            : "-";
        },
        accessor: "man",
        width: 60,
        disableFilters: true,
        // filter: "includesTrimmed",
      },
      {
        Header: "Actual",
        Cell: (cellData) => {
          // console.log(cellData);
          return cellData.row.original.type === "Over/Shortage"
            ? cellData.value
            : "-";
        },
        accessor: "act",
        width: 50,
        // filter: "includesTrimmed",
        disableFilters: true,
      },
      {
        Header: "COD",
        Cell: (cellData) => {
          return cellData.row.original.type === "Redelivery"
            ? currencyFormatter.format(cellData.value)
            : "-";
        },
        accessor: "cod",
        width: 50,
        // filter: "includesTrimmed",
        disableFilters: true,
      },
      {
        Header: "Log Entered",
        accessor: "entered",
        id: "entered",
        Cell: (cellData) => {
          return cellData.value
            ? dateFormat(parseISO(cellData.value), "yyyy-MM-dd hh:mmaa")
            : "";
        },
        width: 100,
        // filter: "includesTrimmed",
        disableFilters: true,
      },
    ],
    [operationLogs]
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    pageCount,
    previousPage,
    setPageSize,
    visibleColumns,
    state: { pageIndex, pageSize },
    toggleSortBy,
    sortedIndex,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      filterTypes,
      initialState: {
        sortBy: [{ id: "entered", desc: true }],
      },
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  );

  return (
    <>
      <div className="table-response order-history-table-container">
        <table className="table col-12" {...getTableProps()}>
          <thead className="order-history-table-header">
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const columnHeaderProps = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );

                    return (
                      <th
                        className="order-history-th"
                        {...columnHeaderProps}
                        onClick={(e) => {
                          if (
                            !columnHeaderProps.onClick || //if the column cannot sort, do nothing on click. If you try to call columnHeaderProps.onClick on a column without sorting or some other onClick set in react table it seems like it will error out.
                            e.target.tagName.toLowerCase() === "input" || //we do not want to toggle the sorting if user tries to click into input or select
                            e.target.tagName.toLowerCase() === "select"
                          ) {
                            return;
                          }
                          columnHeaderProps.onClick(e);
                        }}
                      >
                        {column.render("Header")}
                        {column.canSort && (
                          <SortArrow
                            isSorted={column.isSorted}
                            isSortedDesc={column.isSortedDesc}
                          />
                        )}
                        {column.canFilter && column.render("Filter")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {viewState === viewStates.initial && (
              <tr
                style={{
                  padding: "100px",
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                  textAlign: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <div>
                    <div>Select a date range and submit to view results.</div>
                  </div>
                </td>
              </tr>
            )}
            {viewState === viewStates.loading && (
              <tr
                style={{
                  padding: "100px",
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                  textAlign: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <div>
                    <div className="spinner-border p-3 mb-1" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div>Loading Results</div>
                  </div>
                </td>
              </tr>
            )}
            {viewState === viewStates.error && (
              <tr
                style={{
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <PageLoadError
                    showRefreshButton={false}
                    containerStyle={{ textAlign: "center", padding: "100px" }}
                    errorMessage="There was an issue while loading table data, please try again."
                  />
                </td>
              </tr>
            )}
            {viewState === viewStates.success &&
              (page.length > 0 ? (
                page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()} className="multi-line">
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr
                  style={{
                    padding: "100px",
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    placeContent: "center",
                    textAlign: "center",
                  }}
                >
                  <td style={{ border: "none" }}>
                    <div>
                      <div>No Results Found</div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="col-12">
        <TablePagination
          onPreviousPageClick={() => {
            previousPage();
          }}
          onNextPageClick={() => {
            nextPage();
          }}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={(val) => {
            gotoPage(val);
          }}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          pageCount={pageCount}
          setPageSize={(e) => setPageSize(Number(e.target.value))}
        />
      </div>
    </>
  );
};
